import { MONACO_EDITOR_COLORS } from './monacoThemes';

/**
 * One terminal look for every engine (Warp-style): whichever shell runs
 * underneath (PowerShell, cmd, Git Bash, SSH remote), xterm renders it with
 * the same font, colors, and full ANSI palette. The shell side of the
 * uniform look (no banners, same prompt) is handled in Rust
 * (terminal.rs `build_shell_command`).
 */

export const TERMINAL_FONT_FAMILY =
  '"Cascadia Mono", "Cascadia Code", Consolas, "Courier New", monospace';
/** Default; the live value is the `terminalFontSize` preference
    (Settings → Extensions → Terminal). */
export const TERMINAL_FONT_SIZE = 11;
export const TERMINAL_LINE_HEIGHT = 1.35;

// Hand-tuned ANSI palette — same hues in both themes, lightness flipped, so
// `ls` colors / git output look identical across shells and hosts.
const ANSI_DARK = {
  black: '#1c1c22',
  red: '#f26d78',
  green: '#4ecf8f',
  yellow: '#e5c07b',
  blue: '#6fa8ff',
  magenta: '#c792ea',
  cyan: '#56d4dd',
  white: '#d4d4d8',
  brightBlack: '#5c5c66',
  brightRed: '#ff8a93',
  brightGreen: '#6ee7a8',
  brightYellow: '#f2d38c',
  brightBlue: '#8fbcff',
  brightMagenta: '#dcb0f2',
  brightCyan: '#7ce8ef',
  brightWhite: '#f4f4f5',
};

// The light counterpart (05.10.2026). Same hues, darker shades so every
// colour reads on white: `white`/`brightWhite` become greys (programs use them
// for plain text, which would vanish on a white surface), and yellow/cyan —
// the low-contrast ones on white — are pulled well down. Every entry has at
// least 3:1 contrast on TERMINAL_BACKGROUND_LIGHT (terminalTheme.test.js);
// `black`/`brightBlack` keep the same role as in the dark palette.
const ANSI_LIGHT = {
  black: '#27272a',
  red: '#c4253c',
  green: '#16804f',
  yellow: '#8f6a00',
  blue: '#1d5fd1',
  magenta: '#8e3fb5',
  cyan: '#0b7a85',
  white: '#52525b',
  brightBlack: '#71717a',
  brightRed: '#d63c52',
  brightGreen: '#1f9460',
  brightYellow: '#a07800',
  brightBlue: '#2f6fe0',
  brightMagenta: '#9d4fc4',
  brightCyan: '#0d8792',
  brightWhite: '#3f3f46',
};

/** The dark terminal surface, matching the titlebar's dark color
    (TauriTitleBar dark:bg-[#111113]). */
export const TERMINAL_BACKGROUND = '#111113';
/** The light terminal surface: the light editor's white (monacoThemes). */
export const TERMINAL_BACKGROUND_LIGHT = '#ffffff';

/** Settings → Extensions → Terminal → Theme: follow the app, or always dark / light. */
export const TERMINAL_THEMES = Object.freeze(['follow', 'dark', 'light']);
export const normalizeTerminalTheme = (v) => (TERMINAL_THEMES.includes(v) ? v : 'follow');

/** The xterm theme for a dark or a light terminal (useTerminalIsDark decides which). */
export function buildTerminalTheme(isDark) {
  if (isDark) {
    const colors = MONACO_EDITOR_COLORS.dark;
    return {
      background: TERMINAL_BACKGROUND,
      foreground: colors.foreground,
      cursor: colors.foreground,
      cursorAccent: TERMINAL_BACKGROUND,
      selectionBackground: 'rgba(136, 105, 235, 0.35)',
      ...ANSI_DARK,
    };
  }
  const fg = '#27272a';
  return {
    background: TERMINAL_BACKGROUND_LIGHT,
    foreground: fg,
    cursor: fg,
    cursorAccent: TERMINAL_BACKGROUND_LIGHT,
    selectionBackground: 'rgba(136, 105, 235, 0.22)',
    ...ANSI_LIGHT,
  };
}

/** Shared xterm constructor options — use for every terminal instance. */
export function terminalXtermOptions(isDark, fontSize = TERMINAL_FONT_SIZE, optionAsMeta = false) {
  return {
    // Decorations (the OSC 133 block dividers) are a "proposed" xterm API.
    allowProposedApi: true,
    cursorBlink: true,
    // Lean bar caret (2px) instead of the chunky block — same in every tab.
    cursorStyle: 'bar',
    cursorWidth: 2,
    fontSize: clampTerminalFontSize(fontSize),
    fontFamily: TERMINAL_FONT_FAMILY,
    lineHeight: TERMINAL_LINE_HEIGHT,
    theme: buildTerminalTheme(isDark),
    // macOS: Option sends Meta (ESC-prefixed) for Alt+letter shell bindings,
    // instead of typing ∂ / ƒ. Off by default — on German and other layouts
    // Option types @, [, ], | (Settings → Extensions → Terminal).
    macOptionIsMeta: optionAsMeta === true,
    // With Option as Meta, Option+click still selects text (as in Terminal.app).
    macOptionClickForcesSelection: optionAsMeta === true,
  };
}

export function clampTerminalFontSize(size) {
  const n = Number(size);
  if (!Number.isFinite(n)) return TERMINAL_FONT_SIZE;
  return Math.min(24, Math.max(8, Math.round(n)));
}
